import {Set} from 'immutable';
import _, {chain} from 'lodash';
import memoize from 'lru-memoize';
import React, {Component, ReactNode} from 'react';
import styled from 'styled-components';

import {Trans} from '../../../../../libs/shared/helpers/src/internationalization/react';
import {
  EventHandler,
  Handler,
  HandlerOf,
  PredicateOf,
} from '../../../../../libs/shared/helpers/src/react/reactHelpers';
import {Button} from '../../../../../libs/shared/ui/src/components/buttons/button';
import {ButtonVariantsEnum} from '../../../../../libs/shared/ui/src/components/buttons/buttonConstants';
import {DropdownProps} from '../../../../../libs/shared/ui/src/components/dropdowns/dropdown';
import {DropdownItem} from '../../../../../libs/shared/ui/src/components/dropdowns/dropdownItem';
import {DropdownItemCheckbox} from '../../../../../libs/shared/ui/src/components/dropdowns/dropdownItemCheckbox';
import {DropdownItemContent} from '../../../../../libs/shared/ui/src/components/dropdowns/dropdownItemContent';
import {VisualSizesEnum} from '../../../../../libs/shared/ui/src/styles/commonStyles';
import {AsyncBuffer, AsyncBufferWaiter} from '../../../../helpers/asyncHelpers';
import {InboxModel} from '../../../../models/api/inboxModel';
import {TeamModel} from '../../../../models/api/teamModel';
import {TeamAvatar} from '../../../common/avatars/teamAvatar';
import {OkCancelDialog} from '../../../common/dialogs/okCancelDialog';
import {DeprecatedSearchDropdown} from '../../../common/dropdowns/search/deprecatedSearchDropdown';
import {RowRendererOf} from '../../../common/dropdowns/source/dropdownSource';
import {SyncDropdownSource} from '../../../common/dropdowns/source/syncDropdownSource';

/*
 * Props.
 */

type ToggleInboxHandler = (inboxId: number, saveGate: AsyncBufferWaiter) => void;

interface WorkspaceInboxesDropdownProps extends DropdownProps {
  team: TeamModel;
  hasAddInboxPermission: boolean;
  inboxes: ReadonlyArray<InboxModel>;
  isInboxVisible: PredicateOf<number>;
  navigateToInbox: HandlerOf<number>;
  hideAllInboxes: HandlerOf<AsyncBufferWaiter>;
  toggleInbox: ToggleInboxHandler;
  backButtonLabel?: ReactNode;
  onBackButtonClick?: Handler;
  isEditingWorkspace?: boolean;
}

/*
 * Style.
 */

const StyledHeaderDiv = styled.div`
  display: flex;
  align-items: center;
`;

const StyledTeamNameDiv = styled.div`
  margin-left: 5px;
`;

/*
 * Component.
 */

interface WorkspaceInboxesDropdownState {
  isHideAllConfirmationOpen: boolean;
  processedInboxIds: Set<number>;
  initialInboxIdsChecked: Set<number>;
}

export class WorkspaceInboxesDropdown extends Component<
  WorkspaceInboxesDropdownProps,
  WorkspaceInboxesDropdownState
> {
  constructor(props: WorkspaceInboxesDropdownProps) {
    super(props);

    this.saveBuffer = new AsyncBuffer();
    this.state = {
      isHideAllConfirmationOpen: false,
      processedInboxIds: Set<number>(),
      initialInboxIdsChecked: Set<number>(),
    };
  }

  private readonly saveBuffer: AsyncBuffer;

  /*
   * Lifecycle.
   */

  componentWillUnmount() {
    this.saveBuffer.flush();
  }

  static getDerivedStateFromProps(
    props: Readonly<WorkspaceInboxesDropdownProps>,
    state: Readonly<WorkspaceInboxesDropdownState>,
  ) {
    const currentInboxIds = Set(props.inboxes.map((inbox) => inbox.id));
    // If we processed the current list of inboxes, we do not need to re-process them until one is added/removed
    if (state.processedInboxIds.equals(currentInboxIds)) {
      return null;
    }

    // Get the inboxes that are in a checked state so we can sort them to the top.
    const initialInboxIdsChecked = Set(
      props.inboxes
        .filter((inbox) => {
          // The inbox exists in the current list of inboxes. If the inbox is not contained in this set,
          //  the inbox might have been removed
          const isInboxStillAvailable = currentInboxIds.includes(inbox.id);
          // If the inbox was already marked as checked, we do not want it to move from being sorted first
          const isInboxAlreadyMarkedAsChecked = state.initialInboxIdsChecked.includes(inbox.id);
          const isInboxChecked = props.isInboxVisible(inbox.id);
          const isAlreadyProcessed = state.processedInboxIds.includes(inbox.id);
          return (
            isInboxStillAvailable &&
            (isInboxAlreadyMarkedAsChecked || (isInboxChecked && !isAlreadyProcessed))
          );
        })
        .map((inbox) => inbox.id),
    );
    return {processedInboxIds: currentInboxIds, initialInboxIdsChecked};
  }

  /*
   * Event handlers.
   */

  private readonly onRequestHideAllClick = () => {
    this.setState({isHideAllConfirmationOpen: true});
  };

  private readonly onConfirmHideAllClick = () => {
    this.setState({isHideAllConfirmationOpen: false});
    const saveGate = this.saveBuffer.buildWaiter();
    this.props.hideAllInboxes(saveGate);
  };

  private readonly onCancelHideAllClick = () => {
    this.setState({isHideAllConfirmationOpen: false});
  };

  private renderTitle() {
    const {team} = this.props;
    return (
      <StyledHeaderDiv>
        <TeamAvatar team={team} size={VisualSizesEnum.SMALL} />
        <StyledTeamNameDiv>{team.name} inboxes</StyledTeamNameDiv>
      </StyledHeaderDiv>
    );
  }

  render() {
    const orderedInboxes = _.partition(this.props.inboxes, (inbox) =>
      this.state.initialInboxIdsChecked.includes(inbox.id),
    );
    const dropdownSource = new SyncDropdownSource({
      items: this.props.isEditingWorkspace
        ? [...orderedInboxes[1]]
        : [...orderedInboxes[0], ...orderedInboxes[1]],
      rowFilter: filterRows,
      renderRow: this.renderRow,
    });

    return (
      <>
        <DeprecatedSearchDropdown
          {...this.props}
          title={this.renderTitle()}
          width={this.props.width ?? 300}
          height={this.props.height}
          source={dropdownSource}
          rightIcon={this.maybeRenderHideAllButton()}
        />
        {this.maybeRenderHideAllConfirmation()}
      </>
    );
  }

  private readonly renderRow: RowRendererOf<InboxModel> = (inbox, index) => {
    const {isInboxVisible, toggleInbox, isEditingWorkspace} = this.props;
    const isChecked = isInboxVisible(inbox.id);

    const onInboxSelect: EventHandler = (e) => {
      e.preventDefault(); // Keep the dropdown open
      toggleInbox(inbox.id, this.saveBuffer.buildWaiter());
    };

    return (
      <DropdownItem
        index={index}
        isChecked={isChecked}
        onSelect={onInboxSelect}
        renderRightContent={() => <DropdownItemCheckbox isChecked={isChecked} />}
      >
        <DropdownItemContent isChecked={isChecked} isEditingWorkspace={isEditingWorkspace}>
          {inbox.name}
        </DropdownItemContent>
      </DropdownItem>
    );
  };

  private readonly maybeRenderHideAllButton = () => {
    const {inboxes, isInboxVisible, isEditingWorkspace} = this.props;
    if (isEditingWorkspace) {
      return null;
    }

    const hasPinnedInboxes = inboxes.some((inbox) => isInboxVisible(inbox.id));

    if (!hasPinnedInboxes) {
      return null;
    }

    return (
      <Button
        size={VisualSizesEnum.SMALL}
        variant={ButtonVariantsEnum.LINK}
        onClick={this.onRequestHideAllClick}
      >
        <Trans id="workspace-inboxes-hideall">Hide all</Trans>
      </Button>
    );
  };

  /*
   * Dialog.
   */

  private maybeRenderHideAllConfirmation() {
    if (!this.state.isHideAllConfirmationOpen) {
      return null;
    }

    return (
      <OkCancelDialog
        onRequestClose={this.onCancelHideAllClick}
        title={<Trans id="workspace-inboxes-hideall-confirm-title">Hide all inboxes</Trans>}
        okButtonLabel={<Trans id="workspace-inboxes-hideall-confirm-ok">Hide all</Trans>}
        onOkClick={this.onConfirmHideAllClick}
      >
        <Trans id="workspace-inboxes-hideall-confirm-content">
          Would you like to hide all inboxes from your workspace? This action cannot be undone.
        </Trans>
      </OkCancelDialog>
    );
  }
}

const filterRows = memoize(20)((inboxes: ReadonlyArray<InboxModel>, query: string) => {
  // Shortcut for empty query.
  if (!query) {
    return inboxes;
  }

  // Otherwise, filter by name.
  const lowerCaseQuery = query.toLowerCase();
  return chain(inboxes)
    .compact() // Remove the fixed rows.
    .filter((i) => i.name.toLowerCase().includes(lowerCaseQuery))
    .value();
});
